import { Box, DropdownOption, Icon, TextField, Tooltip } from "@modernatx/ui-kit-react-v2";
import { isUndefined } from "lodash";
import React from "react";

import { FinderBlockProps } from "@/components/V2/Block";
import { MultiSelectDropdown } from "@/components/V2/MultiSelect";

import { useFinder } from "../hooks/useFinderV2";
import { useLocationSearch } from "../hooks/useLocationSearchV2";
import { getValidAgeString } from "../utils";
import { AgePopup } from "./AgePopup";
import { RsvFooterNote } from "./RsvFooterNote";

type SearchProps = FinderBlockProps["search"];

const AGE_LIMIT = 60;
const DRUGS_FOR_AGE_LIMIT = new Set(["RSV"]);

export const Search: React.FC<SearchProps> = ({
  searchInputLabel,
  ageInputLabel,
  typeDropdownLabel,
  typeDropdownOptions,
  clientGeolocationErrorMessages,
  detectLocationDropdownLabel,
  agePopupContent
}) => {
  const {
    age,
    ageSet,
    locationSearchOptionSet,
    searchValue,
    searchValueSet,
    selectedVaccineTypes,
    selectedVaccineTypesSet
  } = useFinder();

  const {
    handleSearchChange,
    searchSuggestionsList,
    handleSearchSuggestionClick,
    showNoResults,
    handleSearchSubmit
  } = useLocationSearch({
    searchValue,
    searchValueSet,
    searchOptionSelect: locationSearchOptionSet,
    clientGeolocationErrorMessages,
    detectLocationDropdownLabel
  });

  const [internalAge, internalAgeSet] = React.useState<number | undefined>(age);
  const [internalVaccineTypes, internalVaccineTypesSet] =
    React.useState<string[]>(selectedVaccineTypes);

  // Debounce the age input to avoid too many state updates
  React.useEffect(() => {
    const timer = setTimeout(() => {
      if (internalAge !== age) {
        ageSet(internalAge);
      }
    }, 500); // 1 second debounce

    return () => clearTimeout(timer);
  }, [internalAge, age, ageSet]);

  // Update the internal age state when the age prop changes
  // This is to ensure that the internal state is in sync with the prop
  React.useEffect(() => {
    internalAgeSet(age);
  }, [age]);

  // Debounce the vaccine types input to avoid too many state updates
  React.useEffect(() => {
    const timer = setTimeout(() => {
      if (internalVaccineTypes.join(",") !== selectedVaccineTypes.join(",")) {
        selectedVaccineTypesSet(internalVaccineTypes);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [internalVaccineTypes, selectedVaccineTypes, selectedVaccineTypesSet]);

  // Update the internal vaccine types state when the selectedVaccineTypes prop changes
  // This is to ensure that the internal state is in sync with the prop
  React.useEffect(() => {
    internalVaccineTypesSet(selectedVaccineTypes);
  }, [selectedVaccineTypes]);

  const productsEligibilityMessage = React.useMemo(() => {
    if (age) {
      if (age < AGE_LIMIT) {
        return "For adults under 60, Moderna's RSV vaccine is only for those with severe underlying health conditions.";
      }
    }
  }, [age]);

  const modifiedDropdownOptions = React.useMemo(() => {
    if (productsEligibilityMessage) {
      return typeDropdownOptions.map((option) => ({
        ...option,
        label: DRUGS_FOR_AGE_LIMIT.has(option.label) ? `${option.label}*` : option.label
      }));
    }
    return typeDropdownOptions;
  }, [productsEligibilityMessage, typeDropdownOptions]);

  const handleAgeChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const numeric = getValidAgeString(e.target.value);
      const ageValue = parseInt(numeric, 10);
      if (!isNaN(ageValue) && ageValue <= 130 && ageValue >= 0) {
        internalAgeSet(ageValue);
      } else if (numeric === "") {
        internalAgeSet(undefined);
      }
    },
    [internalAgeSet]
  );

  const textAge = isUndefined(internalAge) ? "" : String(internalAge);

  const handleTypeSelect = (option: DropdownOption) => {
    const type = option.value as string;

    if (selectedVaccineTypes.includes(type)) {
      internalVaccineTypesSet((prev) => prev.filter((t) => t !== type));
    } else {
      internalVaccineTypesSet((prev) => [...prev, type]);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "spacing-5",
        p: "spacing-5",
        backgroundColor: "white",
        borderRadius: "radius-3"
      }}
    >
      <AgePopup {...agePopupContent} />
      <Box>
        <Box sx={{ position: "relative" }}>
          <TextField
            required={true}
            label={searchInputLabel}
            value={searchValue}
            dropdownOptions={searchSuggestionsList}
            type="select"
            icon="magnifying-glass"
            noResultsMessage={showNoResults ? "no results" : undefined}
            onChange={handleSearchChange}
            onEnter={handleSearchSubmit}
            onSelectOption={handleSearchSuggestionClick}
          />
        </Box>
      </Box>

      <Box sx={{ display: "flex", gap: "spacing-5" }}>
        <Box sx={{ width: () => "33%", position: "relative" }}>
          <Box sx={{ position: "absolute", zIndex: 4, top: 4, right: 2, width: "fit-content" }}>
            <Tooltip
              content="We’ll help you find the right Moderna vaccines based on your age."
              arrowPosition="top-start"
            >
              <Icon icon="info" size="sm" color="text-secondary" />
            </Tooltip>
          </Box>
          <TextField
            required={true}
            value={textAge}
            label={ageInputLabel}
            onChange={handleAgeChange}
          />
        </Box>
        <Box sx={{ width: () => "66%" }}>
          <MultiSelectDropdown
            key="dropdown-vaccine-type"
            value={selectedVaccineTypes}
            label={
              selectedVaccineTypes.length
                ? typeDropdownLabel.selected
                : typeDropdownLabel.unselected
            }
            dropdownOptions={modifiedDropdownOptions}
            footer={
              productsEligibilityMessage ? (
                <RsvFooterNote productsEligibilityMessage={productsEligibilityMessage} />
              ) : null
            }
            onSelectOption={handleTypeSelect}
          />
        </Box>
      </Box>
    </Box>
  );
};
